<script>
import ConfirmDialogue from '@/components/modal/ConfirmDialogue.vue'
import { mdiDotsVertical } from '@mdi/js'
import { displayToast, checkPermission, handleResponseError } from '@/components/utils/globalFunction'
import ExpressistTransporterService from '@/services/transporters/ExpressistTransporterService'
import SvgIcon from '@jamescoyle/vue-icon'

export default {
  name: 'ExpressistConnexionListComponent',
  components: { SvgIcon, ConfirmDialogue },
  props: {
    storeId: Number
  },
  data() {
    return {
      dotVertical: mdiDotsVertical,
      loading: false,

      expressistConnexionLinkList: []
    }
  },
  async mounted() {
    this.expressistConnexionLinkList = []
    await this.getExpressistConnexionLinkListByStoreId(this.storeId)
  },
  methods: {
    checkPermission,

    async getExpressistConnexionLinkListByStoreId(storeId) {
      this.loading = true
      try {
        const response = await ExpressistTransporterService.getExpressistConnexionLinkListByStoreId(
          storeId
        )
        this.expressistConnexionLinkList.push(...response.item_list)
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },

    goToDetail(expressistConnexionLinkId) {
      this.$router.push('/dashboard/transporters/expressist/detail/' + expressistConnexionLinkId)
    },

    async deleteExpressistConnexionLink(linkId) {
      const confirm = await this.$refs.confirmDialogue.show({
        title: this.$t('EXPRESSIST_TRANSPORTER.LIST_PAGE.DELETE_MODAL.TITLE'),
        message: this.$t('EXPRESSIST_TRANSPORTER.LIST_PAGE.DELETE_MODAL.MESSAGE'),
        warning: this.$t('EXPRESSIST_TRANSPORTER.LIST_PAGE.DELETE_MODAL.WARNING'),
        validateButton: this.$t('COMMON.BUTTON.DELETE'),
        cancelButton: this.$t('COMMON.BUTTON.CANCEL')
      })
      if (confirm) {
        try {
          await ExpressistTransporterService.deleteExpressistConnexionLink(linkId)
          displayToast(
            this.$t('EXPRESSIST_TRANSPORTER.LINK_LIST_COMPONENT.MESSAGE.DELETE_SUCCESS'),
            'success'
          )
          this.expressistConnexionLinkList = []
          await this.getExpressistConnexionLinkListByStoreId(this.storeId)
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.loading = false
        }
      }
    }
  }
}
</script>

<template>
  <v-card class="rounded-xl mt-8 custom-card" :border="false" :elevation="0">
    <div class="d-flex flex-column flex-sm-row align-sm-center justify-end">
      <v-btn
        class="boom-button ml-2"
        :to="'/dashboard/transporters/expressist/new/' + this.storeId"
        variant="outlined"
      >
        {{ $t('EXPRESSIST_TRANSPORTER.LIST_PAGE.BUTTON.NEW_EXPRESSIST') }}
        <img src="@/assets/icons/accounts/cross-hover.svg" alt="icône ajouter" class="pl-2" />
      </v-btn>
    </div>

    <!--TRANSPORTERS Connexion Link TABLE-->
    <v-table :hover="true" class="mt-8">
      <!--HEADER TABLE-->
      <thead>
        <tr>
          <th id="connexion_link_name">
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-bold">{{
                $t('EXPRESSIST_TRANSPORTER.LIST_PAGE.TABLE.NAME')
              }}</span>
            </div>
          </th>
          <th id="actions" class="action-btn" />
        </tr>
      </thead>

      <!--BODY TABLE-->
      <tbody>
        <!--LOADING TABLE-->
        <tr v-if="loading">
          <td class="pa-8 pa-md-16 text-md-center" colspan="5">
            {{ $t('EXPRESSIST_TRANSPORTER.LIST_PAGE.TABLE.LOADING_MESSAGE') }}
            <v-progress-linear class="my-5" color="#165039" indeterminate />
          </td>
        </tr>
        <!--EMPTY TABLE-->
        <tr v-else-if="!expressistConnexionLinkList.length">
          <td class="pa-8 pa-md-16 text-md-center" colspan="5">
            {{ $t('EXPRESSIST_TRANSPORTER.LIST_PAGE.TABLE.EMPTY_MESSAGE') }}
            <div class="d-flex align-center justify-center mt-8">
              <img src="@/assets/images/empty.svg" width="250" alt="Empty" />
            </div>
          </td>
        </tr>

        <!--SHOW TRANSPORTERS Connexion Link-->
        <tr
          v-for="connexionLink of expressistConnexionLinkList"
          v-else
          :key="connexionLink.technical_id"
          @click.stop.prevent="goToDetail(connexionLink.technical_id)"
        >
          <!--NAME of expressist transporter connexion link-->
          <td class="font-weight-bold">{{ connexionLink.connexion.transporter.name }}</td>

          <td class="text-right" @click.stop.prevent>
            <v-menu>
              <template v-slot:activator="{ props }">
                <svg-icon
                  v-bind="props"
                  @click.stop.prevent
                  class="icon"
                  type="mdi"
                  :path="dotVertical"
                />
              </template>
              <v-list elevation="1">
                <v-list-item
                  v-if="checkPermission(['DISTRIBUTOR_TRANSPORTER_READ_PERMISSION'])"
                  link
                  :to="'/dashboard/transporters/expressist/detail/' + connexionLink?.technical_id"
                >
                  <v-list-item-title>
                    {{ $t('COMMON.BUTTON.DETAIL') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="checkPermission(['DISTRIBUTOR_TRANSPORTER_UPDATE_PERMISSION'])"
                  link
                  :to="
                    '/dashboard/transporters/expressist/update/' +
                    connexionLink?.connexion.technical_id
                  "
                >
                  <v-list-item-title>
                    {{ $t('COMMON.BUTTON.UPDATE') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="checkPermission(['DISTRIBUTOR_TRANSPORTER_DELETE_PERMISSION'])"
                  link
                >
                  <v-list-item-title
                    @click="deleteExpressistConnexionLink(connexionLink?.technical_id)"
                  >
                    {{ $t('COMMON.BUTTON.DELETE') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </tbody>
    </v-table>
  </v-card>

  <ConfirmDialogue ref="confirmDialogue" />
</template>

<style scoped>
.v-card {
  padding: 32px;
}

:deep(.v-table__wrapper) {
  border-radius: 20px !important;
}

thead {
  background-color: #165039 !important;
}

th > * {
  color: white;
}

tr:nth-of-type(even) {
  background-color: #fafafa;
}

td {
  border-bottom: unset !important;
}

.action-td:hover {
  cursor: pointer;
}

.color-light {
  color: #bfbfbf;
}

.load-more {
  cursor: pointer;
}

.action-btn {
  width: 60px;
}
</style>
